import React, { useEffect, useRef, useState } from 'react';
import { Avatar, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, FormLabel, Grid, InputAdornment, MenuItem, Radio, RadioGroup, Select, Stack, Switch, TextField, Tooltip, Typography } from "@mui/material";
import _ from "lodash";
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Div from "@jumbo/shared/Div";
import moment from 'moment';
import companyServices from 'app/services/company-services';
import RetailCustomersSettings from './RetailCustomersSettings';
import pluginServices from 'app/services/plugin-services';
import JumboCardQuick from '@jumbo/components/JumboCardQuick';
import JumboScrollbar from '@jumbo/components/JumboScrollbar';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import JumboDdMenu from '@jumbo/components/JumboDdMenu';
import Logo from 'app/shared/Logo';
import ImageOrBlank from 'app/widgets/Images/ImageOrBlank';
import AddressAutocomplete from 'app/widgets/Shared/AddressAutocomplete';
import ConfirmDialog from 'app/shared/widgets/ConfirmDialog';
import { LoadingButton } from '@mui/lab';
import { API_HOST, ASSET_IMAGES, HOST } from 'app/utils/constants/paths';
import { normalizeCompanySettings, MOBILE_MODES, SURVEY_RATING_DEFAULT_QUESTION, POD_PHOTO_OPTIONS, PICKING_PACKAGE_PHOTOS_OPTIONS, DRIVER_TRIPS_WINDOW_OPTIONS, CUSTOMER_EMAIL_TYPES, CUSTOMER_EMAIL_DROPOFF } from 'app/utils/companySettings';
import { useCompanySettings } from 'app/providers/CompanySettingsProvider/CompanySettingsProvider';
import { useSnackbar } from 'notistack';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import { DOCUMENT_TYPES } from 'app/utils/documentTypes';

// Los grupos en que se parten los ajustes. Los tabs los dibuja CompanySettings,
// que es quien tambien cuelga Integraciones al lado; aca solo se recibe cual toca.
export const OPERATIONS_SECTION = 'operations';
export const ADMIN_SECTION = 'admin';

// fitContent apaga el scroll interno: la card crece hasta lo que ocupe el contenido.
// Sirve para las secciones cuyo alto depende de los datos (por ejemplo el listado de
// compañías dropoff), donde cualquier alto fijo termina cortando.
const SettingsCard = ({title, children, minScrollHeight = 200, isLoading = false, fitContent = false}) => {
    return (
        <JumboCardQuick
            title={title}
            headerSx={{
                pb: 1,
                '& .MuiCardHeader-action': {
                    my: -.75
                }
            }}
            sx={{mb: 2}}>
            <JumboScrollbar
                disable={fitContent}
                autoHeight
                autoHeightMin={minScrollHeight}>
                <Div sx={{display: isLoading ? 'block' : 'none', textAlign: 'center', my: 5}}>
                    <CircularProgress color="warning"/>
                </Div>
                {children}
            </JumboScrollbar>
        </JumboCardQuick>
    )
}

const DropoffCarrierIcon = ({ carrier }) => {
    const [iconLoadError, setIconLoadError] = useState(false);
    const fallbackLabel = (carrier?.label || carrier?.key || '?').slice(0, 2).toUpperCase();
    const hasIcon = Boolean(carrier?.iconPath) && !iconLoadError;

    useEffect(() => {
        setIconLoadError(false)
    }, [carrier?.iconPath])

    return (
        <Avatar
            variant="rounded"
            sx={{
                width: 34,
                height: 34,
                bgcolor: hasIcon ? 'transparent' : 'grey.300',
                color: 'text.secondary',
                fontSize: 11,
                fontWeight: 700,
            }}>
            {hasIcon ? (
                <img
                    src={carrier.iconPath}
                    alt={carrier.label}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    onError={() => setIconLoadError(true)}
                />
            ) : (
                fallbackLabel
            )}
        </Avatar>
    );
};

const EditCompany = (props) => {
    const {company_id, restrictedView = false, section = OPERATIONS_SECTION} = props;
    const companySettingsContext = useCompanySettings();
    const {authUser} = useJumboAuth();
    // Que tipos de documento ve cada perfil lo decide Kargotools: el backend ignora el
    // campo si lo manda otro perfil, y aca ni se dibuja.
    const isSuperadmin = _.some(authUser?.profiles, { name: 'superadmin' });
    const { enqueueSnackbar } = useSnackbar();
    const [settings, setSettings] = useState(null);
    const [isLoading, setIsLoading] = useState(false)
    const [selectedLogo, setSelectedLogo] = useState()
    const [previewLogo, setPreviewLogo] = useState()
    const [saving, setSaving] = useState(false)
    const [billingPlugins, setBillingPlugins] = useState(null)
    const [togglingBillingPlugin, setTogglingBillingPlugin] = useState(null)
    const [creatingDropoffCarrier, setCreatingDropoffCarrier] = useState(false)
    const [newDropoffCarrierName, setNewDropoffCarrierName] = useState('')
    const [newDropoffCarrierIcon, setNewDropoffCarrierIcon] = useState(null)
    const [newDropoffCarrierIconPreview, setNewDropoffCarrierIconPreview] = useState(null)
    const [dropoffCarrierFormErrors, setDropoffCarrierFormErrors] = useState({})
    const [editingDropoffCarrier, setEditingDropoffCarrier] = useState(null)
    const [editingDropoffCarrierName, setEditingDropoffCarrierName] = useState('')
    const [editingDropoffCarrierIcon, setEditingDropoffCarrierIcon] = useState(null)
    const [editingDropoffCarrierIconPreview, setEditingDropoffCarrierIconPreview] = useState(null)
    const [editingDropoffCarrierAddress, setEditingDropoffCarrierAddress] = useState('')
    const [editingDropoffCarrierErrors, setEditingDropoffCarrierErrors] = useState({})
    const [updatingDropoffCarrier, setUpdatingDropoffCarrier] = useState(false)
    const [deletingDropoffCarrierId, setDeletingDropoffCarrierId] = useState(null)
    const [confirmDeleteDropoffCarrier, setConfirmDeleteDropoffCarrier] = useState(null)
    const inputCompanyLogo = useRef(null);

    useEffect(() => {
        if(isLoading){
            loadCompanySettings()
        }
    }, [isLoading])

    useEffect(() => {
        setIsLoading(true)
        if(company_id && !restrictedView) {
            companyServices.getCompanyPlugins(company_id)
                .then(data => setBillingPlugins(data))
                .catch(() => {})
        }
    }, [company_id, restrictedView])

    useEffect(() => {
        if (!selectedLogo) {
            setPreviewLogo(undefined)
            return
        }

        const objectUrl = URL.createObjectURL(selectedLogo)
        setPreviewLogo(objectUrl)

        // free memory when ever this component is unmounted
        return () => URL.revokeObjectURL(objectUrl)
    }, [selectedLogo])

    useEffect(() => {
        if (!newDropoffCarrierIcon) {
            setNewDropoffCarrierIconPreview(null)
            return
        }

        const objectUrl = URL.createObjectURL(newDropoffCarrierIcon)
        setNewDropoffCarrierIconPreview(objectUrl)

        return () => URL.revokeObjectURL(objectUrl)
    }, [newDropoffCarrierIcon])

    useEffect(() => {
        if (!editingDropoffCarrierIcon) {
            setEditingDropoffCarrierIconPreview(editingDropoffCarrier?.iconPath || null)
            return
        }

        const objectUrl = URL.createObjectURL(editingDropoffCarrierIcon)
        setEditingDropoffCarrierIconPreview(objectUrl)

        return () => URL.revokeObjectURL(objectUrl)
    }, [editingDropoffCarrier, editingDropoffCarrierIcon])

    const loadCompanySettings = () => {
        if(company_id) {
            companyServices.getCompanySettings(company_id)
                .then(data => {
                    setSettings(normalizeCompanySettings(data))
                    if(data?.logo_url) {
                        setPreviewLogo(`${HOST}${data.logo_url}`)
                    }
                })
                .catch(function (error) {})
                .finally(() => {
                    setIsLoading(false)
                });
        }
    }

    const saveCompanySettings = () => {        
        if(settings) {
            setSaving(true)
            
            companyServices.saveCompanySettings(company_id, {...normalizeCompanySettings(settings), new_logo: selectedLogo})
                .then(data => {
                    const normalizedSettings = normalizeCompanySettings(data)
                    setSettings(normalizedSettings)
                    companySettingsContext?.setCompanySettings?.(normalizedSettings)
                    if(data?.logo_url) {
                        setPreviewLogo(`${HOST}${data.logo_url}`)
                    }
                    enqueueSnackbar('Configuracion cambiada con exito', { variant: 'success', autoHideDuration: 4000 })
                })
                .catch(function (error) {
                    enqueueSnackbar(error?.response?.data?.error || 'No fue posible guardar la configuracion', { variant: 'error', autoHideDuration: 4000 })
                })
                .finally(() => {
                    setSaving(false)
                });
        }
    }

    const BILLING_SLUGS = ['bsale'];

    const updateRelbaseSetting = (docType, field, value) => {
        setSettings(prev => ({
            ...prev,
            billing_settings: {
                ...prev.billing_settings,
                relbase: {
                    ...prev.billing_settings.relbase,
                    [docType]: {
                        ...prev.billing_settings.relbase[docType],
                        [field]: value,
                    },
                },
            },
        }));
    };

    const RELBASE_DOC_TYPES = [
        { key: 'sale_notes', label: 'Notas de venta' },
        { key: 'invoices',   label: 'Facturas' },
        { key: 'quotations', label: 'Cotizaciones' },
    ];

    const renderRelbaseBillingSettings = () => {
        if (!settings) return null;
        const relbase = settings.billing_settings?.relbase || {};

        return (
            <React.Fragment>
                <Typography variant="h6" sx={{ mb: 2 }}>Integración Relbase</Typography>
                {RELBASE_DOC_TYPES.map(({ key, label }) => {
                    const doc = relbase[key] || { enabled: false, mode: null };
                    return (
                        <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 2 }} key={key}>
                            <Grid item xs={12} sm={4}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={doc.enabled}
                                            onChange={(e) => {
                                                updateRelbaseSetting(key, 'enabled', e.target.checked);
                                                if (!e.target.checked) {
                                                    updateRelbaseSetting(key, 'mode', null);
                                                }
                                            }}
                                            color="success"
                                        />
                                    }
                                    label={<Typography variant="subtitle2">{label}</Typography>}
                                />
                            </Grid>
                            {doc.enabled && (
                                <Grid item xs={12} sm={8}>
                                    <RadioGroup
                                        value={doc.mode || ''}
                                        onChange={(e) => updateRelbaseSetting(key, 'mode', e.target.value)}>
                                        <FormControlLabel
                                            value="haulers"
                                            control={<Radio size="small" />}
                                            label={<Typography variant="body2">Facturación desde Haulers</Typography>}
                                        />
                                        <FormControlLabel
                                            value="external"
                                            control={<Radio size="small" />}
                                            label={<Typography variant="body2">Por facturar</Typography>}
                                        />
                                    </RadioGroup>
                                </Grid>
                            )}
                        </Grid>
                    );
                })}
            </React.Fragment>
        );
    };

    const toggleBillingPlugin = (companyPluginId) => {
        setTogglingBillingPlugin(companyPluginId)
        pluginServices.togglePluginActivation(companyPluginId)
            .then(data => {
                setBillingPlugins(prev => prev.map(cp =>
                    cp.id === companyPluginId ? {...cp, enabled: data.enabled} : cp
                ))
                enqueueSnackbar('Integración actualizada', { variant: 'success', autoHideDuration: 4000 })
            })
            .catch(() => {
                enqueueSnackbar('No fue posible actualizar la integración', { variant: 'error', autoHideDuration: 4000 })
            })
            .finally(() => setTogglingBillingPlugin(null))
    }

    const renderBillingSettings = () => {
        const filtered = (billingPlugins || []).filter(cp => BILLING_SLUGS.includes(cp.plugin?.slug))

        return (
            <React.Fragment>
                <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                    Activa la integración para generar facturas desde Haulers
                </Typography>
                {billingPlugins === null ? (
                    <Div sx={{textAlign: 'center', my: 2}}>
                        <CircularProgress color="warning" size={24}/>
                    </Div>
                ) : (
                    filtered.map(cp => (
                        <Grid container spacing={2} alignItems="center" sx={{mb: 2}} key={cp.id}>
                            <Grid item xs={7}>
                                <Typography variant="subtitle2">{cp.plugin?.name}</Typography>
                            </Grid>
                            <Grid item xs={5}>
                                <LoadingButton
                                    loading={togglingBillingPlugin === cp.id}
                                    size="small"
                                    variant="outlined"
                                    color={cp.enabled ? "success" : "warning"}
                                    onClick={() => toggleBillingPlugin(cp.id)}>
                                    {cp.enabled ? "Activado" : "No activado"}
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    ))
                )}
                {renderRelbaseBillingSettings()}
            </React.Fragment>
        )
    }

    const renderSelectOpts = (opts) => {
        return opts.map((item, index) => (<MenuItem value={item}>{item}</MenuItem>))
    }

    const onSelectLogo = e => {
        if (!e.target.files || e.target.files.length === 0) {
            setSelectedLogo(undefined)
            return
        }

        setSelectedLogo(e.target.files[0])
    }

    const onSelectDropoffCarrierIcon = (e) => {
        if (!e.target.files || e.target.files.length === 0) {
            setNewDropoffCarrierIcon(null)
            return
        }

        setNewDropoffCarrierIcon(e.target.files[0])
        setDropoffCarrierFormErrors((prev) => ({ ...prev, icon: null }))
    }

    const resetDropoffCarrierForm = () => {
        setNewDropoffCarrierName('')
        setNewDropoffCarrierIcon(null)
        setDropoffCarrierFormErrors({})
    }

    const createDropoffCarrier = () => {
        const normalizedName = newDropoffCarrierName.trim()
        const nextErrors = {}

        if (!normalizedName) {
            nextErrors.name = 'Ingresa el nombre de la compañía'
        }

        if (!newDropoffCarrierIcon) {
            nextErrors.icon = 'Selecciona una imagen'
        }

        if (Object.keys(nextErrors).length > 0) {
            setDropoffCarrierFormErrors(nextErrors)
            return
        }

        setCreatingDropoffCarrier(true)

        companyServices.createDropoffCarrier(company_id, {
            name: normalizedName,
            icon: newDropoffCarrierIcon,
        })
            .then((data) => {
                const normalizedSettings = normalizeCompanySettings(data)
                setSettings(normalizedSettings)
                companySettingsContext?.setCompanySettings?.(normalizedSettings)
                resetDropoffCarrierForm()
                enqueueSnackbar('Compañía dropoff creada correctamente', { variant: 'success', autoHideDuration: 4000 })
            })
            .catch((error) => {
                enqueueSnackbar(error?.response?.data?.error || 'No fue posible crear la compañía dropoff', { variant: 'error', autoHideDuration: 4000 })
            })
            .finally(() => {
                setCreatingDropoffCarrier(false)
            })
    }

    const startEditingDropoffCarrier = (carrier) => {
        setEditingDropoffCarrier(carrier)
        setEditingDropoffCarrierName(carrier?.label || '')
        setEditingDropoffCarrierIcon(null)
        setEditingDropoffCarrierIconPreview(carrier?.iconPath || null)
        setEditingDropoffCarrierAddress(carrier?.defaultAddress?.text || '')
        setEditingDropoffCarrierErrors({})
    }

    const cancelEditingDropoffCarrier = () => {
        setEditingDropoffCarrier(null)
        setEditingDropoffCarrierName('')
        setEditingDropoffCarrierIcon(null)
        setEditingDropoffCarrierIconPreview(null)
        setEditingDropoffCarrierAddress('')
        setEditingDropoffCarrierErrors({})
    }

    const onSelectEditingDropoffCarrierIcon = (e) => {
        if (!e.target.files || e.target.files.length === 0) {
            setEditingDropoffCarrierIcon(null)
            setEditingDropoffCarrierIconPreview(editingDropoffCarrier?.iconPath || null)
            return
        }

        setEditingDropoffCarrierIcon(e.target.files[0])
        setEditingDropoffCarrierErrors((prev) => ({ ...prev, icon: null }))
    }

    const updateDropoffCarrier = () => {
        if (!editingDropoffCarrier) return

        // Los carriers del catálogo base no tienen nombre ni ícono editables: de
        // ellos sólo se configura la dirección de dropoff por defecto.
        const isBaseCarrier = !editingDropoffCarrier.custom
        const normalizedName = editingDropoffCarrierName.trim()
        const nextErrors = {}

        if (!isBaseCarrier && !normalizedName) {
            nextErrors.name = 'Ingresa el nombre de la compañía'
        }

        if (Object.keys(nextErrors).length > 0) {
            setEditingDropoffCarrierErrors(nextErrors)
            return
        }

        setUpdatingDropoffCarrier(true)

        companyServices.updateDropoffCarrier(company_id, editingDropoffCarrier.id || editingDropoffCarrier.key, {
            name: isBaseCarrier ? undefined : normalizedName,
            icon: isBaseCarrier ? null : editingDropoffCarrierIcon,
            address: editingDropoffCarrierAddress.trim(),
        })
            .then((data) => {
                const normalizedSettings = normalizeCompanySettings(data)
                setSettings(normalizedSettings)
                companySettingsContext?.setCompanySettings?.(normalizedSettings)
                cancelEditingDropoffCarrier()
                enqueueSnackbar('Compañía dropoff actualizada correctamente', { variant: 'success', autoHideDuration: 4000 })
            })
            .catch((error) => {
                enqueueSnackbar(error?.response?.data?.error || 'No fue posible actualizar la compañía dropoff', { variant: 'error', autoHideDuration: 4000 })
            })
            .finally(() => {
                setUpdatingDropoffCarrier(false)
            })
    }

    const deleteDropoffCarrier = (carrier) => {
        if (!carrier?.id) return

        setConfirmDeleteDropoffCarrier(null)
        setDeletingDropoffCarrierId(carrier.id)

        companyServices.deleteDropoffCarrier(company_id, carrier.id)
            .then((data) => {
                const normalizedSettings = normalizeCompanySettings(data)
                setSettings(normalizedSettings)
                companySettingsContext?.setCompanySettings?.(normalizedSettings)
                if (editingDropoffCarrier?.id === carrier.id) {
                    cancelEditingDropoffCarrier()
                }
                enqueueSnackbar('Compañía dropoff eliminada correctamente', { variant: 'success', autoHideDuration: 4000 })
            })
            .catch((error) => {
                enqueueSnackbar(error?.response?.data?.error || 'No fue posible eliminar la compañía dropoff', { variant: 'error', autoHideDuration: 4000 })
            })
            .finally(() => {
                setDeletingDropoffCarrierId(null)
            })
    }

    const renderCompanySettings = () => {
        if(settings) {
            return (
                <React.Fragment>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6} lg={2} textAlign={"center"}>
                            <Stack direction="column" justifyContent="center" alignItems="center" spacing={1}>
                                <Button onClick={() => { inputCompanyLogo.current.click(); } }>Cambiar logo</Button>
                                <ImageOrBlank src={previewLogo} size="100" style={{maxWidth: 150}} />
                                <input type='file' id='company-logo' ref={inputCompanyLogo} style={{display: 'none'}} onChange={onSelectLogo}/>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={6} lg={4}>
                            <Typography variant="h6" sx={{mb: 2}}>Datos de la empresa</Typography>

                            <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2}}>
                                <Grid item xs={6} lg={4}>
                                    <FormLabel>Nombre</FormLabel>
                                </Grid>
                                <Grid item xs={6} lg={8}>
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        error={false}
                                        variant="standard"
                                        value={settings.name}
                                        onChange={(e) => setSettings({...settings, name: e.target.value})}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2}}>
                                <Grid item xs={6} lg={4}>
                                    <FormLabel>TIN</FormLabel>
                                </Grid>
                                <Grid item xs={6} lg={8}>
                                    <TextField
                                        fullWidth
                                        error={false}
                                        variant="standard"
                                        helperText="Tax Identification Number"
                                        value={settings.tin}
                                        onChange={(e) => setSettings({...settings, tin: e.target.value})}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12} md={6} lg={4}>
                            <Typography variant="h6" sx={{mb: 2}}>Datos de contacto</Typography>

                            <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2}}>
                                <Grid item xs={6} lg={4}>
                                    <FormLabel>Email</FormLabel>
                                </Grid>
                                <Grid item xs={6} lg={8}>
                                    <TextField
                                        fullWidth
                                        error={false}
                                        variant="standard"
                                        value={settings.contact_email}
                                        onChange={(e) => setSettings({...settings, contact_email: e.target.value})}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2}}>
                                <Grid item xs={6} lg={4}>
                                    <FormLabel>Teléfono</FormLabel>
                                </Grid>
                                <Grid item xs={6} lg={8}>
                                    <TextField
                                        fullWidth
                                        error={false}
                                        variant="standard"
                                        value={settings.contact_phone}
                                        onChange={(e) => setSettings({...settings, contact_phone: e.target.value})}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2}}>
                                <Grid item xs={6} lg={4}>
                                    <FormLabel>Whatsapp</FormLabel>
                                </Grid>
                                <Grid item xs={6} lg={8}>
                                    <TextField
                                        fullWidth
                                        error={false}
                                        variant="standard"
                                        value={settings.whatsapp_number}
                                        onChange={(e) => setSettings({...settings, whatsapp_number: e.target.value})}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </React.Fragment>
            )
        }
    }

    const renderPlanificationSettings = () => {
        if(settings) {
            return (
                <React.Fragment>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={7}>
                            <Typography variant="h6" sx={{mb: 2}}>Compañías dropoff</Typography>
                            {renderDropoffCarrierSettings()}
                        </Grid>

                        <Grid item xs={12} md={5}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2}}>
                                <Typography variant="h6" sx={{mb: 0}}>Campos obligatorios para crear planificación</Typography>
                                <Tooltip
                                    arrow
                                    title="Define qué información será obligatoria ingresar al momento de planificar los despachos">
                                    <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
                                </Tooltip>
                            </Stack>

                            <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2, maxWidth: 420}}>
                                <Grid item xs={6} lg={5}>
                                    <FormLabel>Vehículo</FormLabel>
                                </Grid>
                                <Grid item xs={6} lg={7}>
                                    <RadioGroup row>
                                        <FormControlLabel value={true} control={<Radio checked={settings.plan_require_vehicle === true} onClick={(e) => setSettings({...settings, plan_require_vehicle: true})}/>} label="Si"/>
                                        <FormControlLabel value={false} control={<Radio checked={settings.plan_require_vehicle === false} onClick={(e) => setSettings({...settings, plan_require_vehicle: false})}/>} label="No"/>
                                    </RadioGroup>
                                </Grid>
                            </Grid>

                            <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2, maxWidth: 420}}>
                                <Grid item xs={6} lg={5}>
                                    <FormLabel>Conductor</FormLabel>
                                </Grid>
                                <Grid item xs={6} lg={7}>
                                    <RadioGroup row>
                                        <FormControlLabel value={true} control={<Radio checked={settings.plan_require_driver === true} onClick={(e) => setSettings({...settings, plan_require_driver: true})}/>} label="Si"/>
                                        <FormControlLabel value={false} control={<Radio checked={settings.plan_require_driver === false} onClick={(e) => setSettings({...settings, plan_require_driver: false})}/>} label="No"/>
                                    </RadioGroup>
                                </Grid>
                            </Grid>

                            <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2, maxWidth: 420}}>
                                <Grid item xs={6} lg={5}>
                                    <FormLabel>Transportista</FormLabel>
                                </Grid>
                                <Grid item xs={6} lg={7}>
                                    <RadioGroup row>
                                        <FormControlLabel value={true} control={<Radio checked={settings.plan_require_transportation_company === true} onClick={() => setSettings({...settings, plan_require_transportation_company: true})}/>} label="Si"/>
                                        <FormControlLabel value={false} control={<Radio checked={settings.plan_require_transportation_company === false} onClick={() => setSettings({...settings, plan_require_transportation_company: false})}/>} label="No"/>
                                    </RadioGroup>
                                </Grid>
                            </Grid>

                        </Grid>
                    </Grid>
                </React.Fragment>
            )
        }
    }

    const updateMobileSetting = (field, value) => {
        setSettings(prev => ({
            ...prev,
            mobile_settings: {
                ...prev.mobile_settings,
                [field]: value,
            },
        }));
    };

    const MOBILE_APP_SECTIONS = [
        { key: 'picking_mode',     label: 'Picking' },
        { key: 'preparation_mode', label: 'Preparación' },
    ];

    const MOBILE_MODE_OPTIONS = [
        { value: MOBILE_MODES.ORDER, label: 'Por pedido' },
        { value: MOBILE_MODES.TRIP,  label: 'Por viaje' },
    ];

    const renderMobileAppSettings = () => {
        if (!settings) return null;
        const ms = settings.mobile_settings || {};

        return (
            <React.Fragment>
                <Grid container spacing={3}>
                    {MOBILE_APP_SECTIONS.map(({ key, label }) => (
                        <Grid item xs={12} sm={6} md={4} key={key}>
                            <Typography variant="h6" sx={{ mb: 2 }}>{label}</Typography>
                            <RadioGroup
                                value={ms[key] || MOBILE_MODES.ORDER}
                                onChange={(e) => updateMobileSetting(key, e.target.value)}>
                                {MOBILE_MODE_OPTIONS.map(({ value, label: optLabel }) => (
                                    <FormControlLabel
                                        key={value}
                                        value={value}
                                        control={<Radio size="small" />}
                                        label={<Typography variant="body2">{optLabel}</Typography>}
                                    />
                                ))}
                            </RadioGroup>
                        </Grid>
                    ))}

                    <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Pickeo</Typography>
                        <FormLabel sx={{ display: 'block', mb: 1 }}>Límite de pickeo manual</FormLabel>
                        <TextField
                            error={false}
                            variant="standard"
                            type="number"
                            inputProps={{ min: 1, step: 1 }}
                            helperText="Unidades (por defecto: 30)"
                            value={settings.manual_picking_limit_units ?? 30}
                            onChange={(e) => setSettings({
                                ...settings,
                                manual_picking_limit_units: e.target.value === '' ? '' : Number(e.target.value)
                            })}
                        />

                        <FormLabel sx={{ display: 'block', mt: 3, mb: 1 }}>Foto de bultos al terminar</FormLabel>
                        <Select
                            fullWidth
                            variant="standard"
                            value={settings.picking_package_photos || 'off'}
                            onChange={(e) => setSettings({ ...settings, picking_package_photos: e.target.value })}
                        >
                            {PICKING_PACKAGE_PHOTOS_OPTIONS.map(({ value, label: optLabel }) => (
                                <MenuItem key={value} value={value}>{optLabel}</MenuItem>
                            ))}
                        </Select>
                        <Typography variant="caption" color="text.secondary">
                            {PICKING_PACKAGE_PHOTOS_OPTIONS.find((o) => o.value === (settings.picking_package_photos || 'off'))?.help}
                        </Typography>
                    </Grid>
                </Grid>
            </React.Fragment>
        );
    };

    const renderDropoffCarrierSettings = () => {
        if (!settings) return null;

        const dropoffCarrierCatalog = settings.available_dropoff_carriers || [];
        const enabledDropoffCarriers = new Set(settings.dropoff_carriers || []);

        if (dropoffCarrierCatalog.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary">
                    No hay compañías dropoff disponibles.
                </Typography>
            );
        }

        return (
            <React.Fragment>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Crea compañías dropoff personalizadas para esta empresa y úsalas en planificación y viajes.
                </Typography>

                <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 2.5 }}>
                    <Grid item xs={12} md={5}>
                        <TextField
                            fullWidth
                            label="Nombre de compañía"
                            size="small"
                            value={newDropoffCarrierName}
                            error={Boolean(dropoffCarrierFormErrors.name)}
                            helperText={dropoffCarrierFormErrors.name || ''}
                            onChange={(e) => {
                                setNewDropoffCarrierName(e.target.value)
                                setDropoffCarrierFormErrors((prev) => ({ ...prev, name: null }))
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button variant="outlined" component="label" size="small">
                                {newDropoffCarrierIcon ? 'Cambiar imagen' : 'Seleccionar imagen'}
                                <input
                                    hidden
                                    type="file"
                                    accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,image/svg+xml"
                                    onChange={onSelectDropoffCarrierIcon}
                                />
                            </Button>
                            <DropoffCarrierIcon carrier={{ label: newDropoffCarrierName || '??', iconPath: newDropoffCarrierIconPreview }} />
                        </Stack>
                        {dropoffCarrierFormErrors.icon && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                {dropoffCarrierFormErrors.icon}
                            </Typography>
                        )}
                    </Grid>

                    <Grid item xs={12} md={2}>
                        <LoadingButton
                            loading={creatingDropoffCarrier}
                            variant="contained"
                            size="small"
                            fullWidth
                            onClick={createDropoffCarrier}>
                            Agregar
                        </LoadingButton>
                    </Grid>
                </Grid>

                {/* El listado crece con cada compañía que agregue la empresa. Se acota acá
                    y no en la card entera para que las dos columnas mantengan un alto
                    parecido: si no, la izquierda termina en Pickeo y queda un vacío largo. */}
                <Box sx={{ maxHeight: 320, overflowY: 'auto', overflowX: 'hidden', pr: 0.5 }}>
                    <Grid container spacing={1.5}>
                        {dropoffCarrierCatalog.map((carrier) => {
                        const isEnabled = enabledDropoffCarriers.has(carrier.key);

                        return (
                            <Grid item xs={12} sm={6} key={carrier.key}>
                                <Stack
                                    direction="row"
                                    spacing={1.5}
                                    alignItems="center"
                                    onClick={() => startEditingDropoffCarrier(carrier)}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: isEnabled ? 'success.light' : 'divider',
                                        borderRadius: 2,
                                        px: 1.5,
                                        py: 1,
                                        cursor: 'pointer',
                                        '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                                    }}>
                                    <DropoffCarrierIcon carrier={carrier} />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }} noWrap>
                                            {carrier.label || carrier.key}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color={carrier.defaultAddress ? 'text.secondary' : 'text.disabled'}
                                            sx={{ display: 'block', mt: 0.25 }}
                                            noWrap>
                                            {carrier.defaultAddress?.text || 'Sin dirección por defecto'}
                                        </Typography>
                                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                                            <Chip
                                                size="small"
                                                label={isEnabled ? 'Habilitada' : 'No habilitada'}
                                                color={isEnabled ? 'success' : 'default'}
                                                variant={isEnabled ? 'filled' : 'outlined'}
                                            />
                                            {carrier.custom && (
                                                <Chip
                                                    size="small"
                                                    label="Personalizada"
                                                    color="info"
                                                    variant="outlined"
                                                />
                                            )}
                                        </Stack>
                                    </Box>
                                    {carrier.custom && (
                                        <Stack direction="row" spacing={0.5} sx={{ ml: 'auto' }}>
                                            <LoadingButton
                                                loading={deletingDropoffCarrierId === carrier.id}
                                                size="small"
                                                color="error"
                                                variant="text"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    setConfirmDeleteDropoffCarrier(carrier)
                                                }}>
                                                Eliminar
                                            </LoadingButton>
                                        </Stack>
                                    )}
                                </Stack>
                            </Grid>
                        );
                        })}
                    </Grid>
                </Box>
            </React.Fragment>
        );
    };

    const renderDropoffCarrierEditModal = () => {
        if (!editingDropoffCarrier) return null;

        // Los carriers del catálogo base viven en constantes del backend, no en
        // company_dropoff_carriers: su nombre e ícono no se pueden editar.
        const isBaseCarrier = !editingDropoffCarrier.custom;

        return (
            <Dialog
                fullWidth
                maxWidth="sm"
                open={Boolean(editingDropoffCarrier)}
                onClose={updatingDropoffCarrier ? undefined : cancelEditingDropoffCarrier}>
                <DialogTitle>
                    Editar compañía dropoff
                </DialogTitle>
                <DialogContent>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                        <DropoffCarrierIcon carrier={{ label: editingDropoffCarrierName || '??', iconPath: editingDropoffCarrierIconPreview }} />
                        <Typography variant="body2" color="text.secondary">
                            {editingDropoffCarrier.label || editingDropoffCarrier.key}
                        </Typography>
                    </Stack>
                    <Grid container spacing={2} alignItems="flex-start" sx={{ mt: 0 }}>
                        {!isBaseCarrier && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Nombre de compañía"
                                    size="small"
                                    value={editingDropoffCarrierName}
                                    error={Boolean(editingDropoffCarrierErrors.name)}
                                    helperText={editingDropoffCarrierErrors.name || ''}
                                    onChange={(e) => {
                                        setEditingDropoffCarrierName(e.target.value)
                                        setEditingDropoffCarrierErrors((prev) => ({ ...prev, name: null }))
                                    }}
                                />
                            </Grid>
                        )}
                        {!isBaseCarrier && (
                            <Grid item xs={12}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Button variant="outlined" component="label" size="small">
                                        {editingDropoffCarrierIcon ? 'Cambiar imagen' : 'Reemplazar imagen'}
                                        <input
                                            hidden
                                            type="file"
                                            accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,image/svg+xml"
                                            onChange={onSelectEditingDropoffCarrierIcon}
                                        />
                                    </Button>
                                    <DropoffCarrierIcon carrier={{ label: editingDropoffCarrierName || '??', iconPath: editingDropoffCarrierIconPreview }} />
                                </Stack>
                                {editingDropoffCarrierErrors.icon && (
                                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                        {editingDropoffCarrierErrors.icon}
                                    </Typography>
                                )}
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <AddressAutocomplete
                                label="Dirección de dropoff por defecto"
                                value={editingDropoffCarrierAddress}
                                onChange={setEditingDropoffCarrierAddress}
                                helperText="Busca y selecciona una dirección. Déjala vacía para quitarla."
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={updatingDropoffCarrier}
                        onClick={cancelEditingDropoffCarrier}>
                        Cancelar
                    </Button>
                    <LoadingButton
                        loading={updatingDropoffCarrier}
                        variant="contained"
                        size="small"
                        onClick={updateDropoffCarrier}>
                        Guardar
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        );
    };

    const renderNotifSettings = () => {
        if(settings) {
            const dropoffEnabled = settings.customer_email_settings?.[CUSTOMER_EMAIL_DROPOFF.key] !== false;
            const setDropoffEnabled = (value) => setSettings({
                ...settings,
                customer_email_settings: { ...settings.customer_email_settings, [CUSTOMER_EMAIL_DROPOFF.key]: value },
            });

            return (
                <React.Fragment>
                    <Grid container spacing={3}>
                        <Grid item xs={12} lg={7}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1}}>
                                <Typography variant="h6" sx={{mb: 0}}>Correos a clientes</Typography>
                                <Tooltip
                                    arrow
                                    title="Avisos que le llegan al cliente a los correos de su ficha. Las facturas y el aviso de entrega parcial al vendedor no dependen de esto.">
                                    <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
                                </Tooltip>
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                                Elige cuáles se envían y con qué asunto.
                            </Typography>

                            {CUSTOMER_EMAIL_TYPES.map(({ key, label, subjectField, fixedSubject, help }) => {
                                const enabled = settings.customer_email_settings?.[key] !== false;
                                const setEnabled = (value) => setSettings({
                                    ...settings,
                                    customer_email_settings: { ...settings.customer_email_settings, [key]: value },
                                });

                                return (
                                    <Grid key={key} container spacing={2} alignItems={"center"} sx={{mb: 1}}>
                                        <Grid item xs={12} sm={4}>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <FormLabel>{label}</FormLabel>
                                                <Tooltip arrow title={help}>
                                                    <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
                                                </Tooltip>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={5} sm={3}>
                                            <RadioGroup row>
                                                <FormControlLabel value={true} control={<Radio checked={enabled} onClick={() => setEnabled(true)}/>} label="Si"/>
                                                <FormControlLabel value={false} control={<Radio checked={!enabled} onClick={() => setEnabled(false)}/>} label="No"/>
                                            </RadioGroup>
                                        </Grid>
                                        <Grid item xs={7} sm={5}>
                                            {subjectField ? (
                                                <TextField
                                                    fullWidth
                                                    error={false}
                                                    variant="standard"
                                                    label="Asunto"
                                                    disabled={!enabled}
                                                    value={settings[subjectField]}
                                                    onChange={(e) => setSettings({...settings, [subjectField]: e.target.value})}
                                                />
                                            ) : (
                                                <Typography variant="caption" color={enabled ? 'text.secondary' : 'text.disabled'}>Asunto: {fixedSubject}</Typography>
                                            )}
                                        </Grid>
                                    </Grid>
                                );
                            })}

                            <Grid container spacing={2} alignItems={"center"} sx={{mt: 1, pt: 1, borderTop: 1, borderColor: 'divider'}}>
                                <Grid item xs={12} sm={4}>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <FormLabel>{CUSTOMER_EMAIL_DROPOFF.label}</FormLabel>
                                        <Tooltip arrow title={CUSTOMER_EMAIL_DROPOFF.help}>
                                            <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
                                        </Tooltip>
                                    </Stack>
                                </Grid>
                                <Grid item xs={5} sm={3}>
                                    <RadioGroup row>
                                        <FormControlLabel value={true} control={<Radio checked={dropoffEnabled} onClick={() => setDropoffEnabled(true)}/>} label="Si"/>
                                        <FormControlLabel value={false} control={<Radio checked={!dropoffEnabled} onClick={() => setDropoffEnabled(false)}/>} label="No"/>
                                    </RadioGroup>
                                </Grid>
                                <Grid item xs={7} sm={5}>
                                    <Typography variant="caption" color="text.secondary">Aplica a todos los avisos de arriba</Typography>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12} lg={5}>
                            <Typography variant="h6" sx={{mb: 2}}>Configuración correo SMTP</Typography>

                            <Grid container spacing={3} alignItems={"center"} sx={{mb: 4}}>
                                <Grid item xs={6} lg={6}>
                                    <FormLabel>Usar mi propio servidor de correo</FormLabel>
                                </Grid>
                                <Grid item xs={6} lg={6}>
                                    <RadioGroup row>
                                        <FormControlLabel value={true} control={<Radio checked={settings.use_custom_mailer === true} onClick={(e) => setSettings({...settings, use_custom_mailer: true})}/>} label="Si"/>
                                        <FormControlLabel value={false} control={<Radio checked={settings.use_custom_mailer === false} onClick={(e) => setSettings({...settings, use_custom_mailer: false})}/>} label="No"/>
                                    </RadioGroup>
                                </Grid>
                            </Grid>

                            {settings.use_custom_mailer && (
                                <React.Fragment>
                                    <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2}}>
                                        <Grid item xs={6} lg={4}>
                                            <FormLabel>Servidor de correo saliente</FormLabel>
                                        </Grid>
                                        <Grid item xs={6} lg={8}>
                                            <TextField
                                                fullWidth
                                                error={false}
                                                variant="standard"
                                                helperText="Ej: smtp.gmail.com"
                                                value={settings.mailer_smtp_address}
                                                onChange={(e) => setSettings({...settings, mailer_smtp_address: e.target.value})}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2}}>
                                        <Grid item xs={6} lg={4}>
                                            <FormLabel>Puerto</FormLabel>
                                        </Grid>
                                        <Grid item xs={6} lg={8}>
                                            <TextField
                                                fullWidth
                                                error={false}
                                                variant="standard"
                                                helperText="Ej: 587"
                                                value={settings.mailer_smtp_port}
                                                onChange={(e) => setSettings({...settings, mailer_smtp_port: e.target.value})}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2}}>
                                        <Grid item xs={6} lg={4}>
                                            <FormLabel>Dominio</FormLabel>
                                        </Grid>
                                        <Grid item xs={6} lg={8}>
                                            <TextField
                                                fullWidth
                                                error={false}
                                                variant="standard"
                                                helperText={`Ej: ${_.kebabCase(settings.name) || 'tudominio' }.com`}
                                                value={settings.mailer_smtp_domain}
                                                onChange={(e) => setSettings({...settings, mailer_smtp_domain: e.target.value})}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2}}>
                                        <Grid item xs={6} lg={4}>
                                            <FormLabel>Autenticación</FormLabel>
                                        </Grid>
                                        <Grid item xs={6} lg={8}>
                                            <Select
                                                value={settings.mailer_smtp_authentication}
                                                fullWidth
                                                variant="standard"
                                                onChange={(e) => setSettings({...settings, mailer_smtp_authentication: e.target.value})}>
                                                    {renderSelectOpts(['plain','login', 'cram-md5', 'digest-md5', 'ntlm', 'gssapi', 'xoauth', 'xoauth2'])}
                                            </Select>
                                        </Grid>
                                    </Grid>

                                    <Grid container spacing={3} alignItems={"center"}>
                                        <Grid item xs={6} lg={4}>
                                            <FormLabel>StartTLS Auto</FormLabel>
                                        </Grid>
                                        <Grid item xs={6} lg={8}>
                                            <RadioGroup row>
                                                <FormControlLabel value={true} control={<Radio checked={settings.mailer_smtp_enable_starttls_auto === true} onClick={(e) => setSettings({...settings, mailer_smtp_enable_starttls_auto: true})}/>} label="Si"/>
                                                <FormControlLabel value={false} control={<Radio checked={settings.mailer_smtp_enable_starttls_auto === false} onClick={(e) => setSettings({...settings, mailer_smtp_enable_starttls_auto: false})}/>} label="No"/>
                                            </RadioGroup>
                                        </Grid>
                                    </Grid>

                                    <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2}}>
                                        <Grid item xs={6} lg={4}>
                                            <FormLabel>Usuario</FormLabel>
                                        </Grid>
                                        <Grid item xs={6} lg={8}>
                                            <TextField
                                                fullWidth
                                                error={false}
                                                variant="standard"
                                                helperText={`Ej: contacto@${_.kebabCase(settings.name) || 'tudominio' }.com`}
                                                value={settings.mailer_smtp_user_name}
                                                onChange={(e) => setSettings({...settings, mailer_smtp_user_name: e.target.value})}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2}}>
                                        <Grid item xs={6} lg={4}>
                                            <FormLabel>Contraseña</FormLabel>
                                        </Grid>
                                        <Grid item xs={6} lg={8}>
                                            <TextField
                                                fullWidth
                                                error={false}
                                                type="password"
                                                variant="standard"
                                                helperText="Tu contraseña será almacenada encriptada en nuestra base de datos"
                                                value={settings.mailer_smtp_password}
                                                onChange={(e) => setSettings({...settings, mailer_smtp_password: e.target.value})}
                                            />
                                        </Grid>
                                    </Grid>
                                </React.Fragment>
                            )}
                            

                        </Grid>
                    </Grid>
                </React.Fragment>
            )
        }
    }
    
    /**
     * Evaluacion del servicio: el modal de estrellas que la app movil le muestra al
     * cliente despues de firmar la entrega.
     *
     * El texto no es una columna de `companies` como los asuntos de correo: el backend
     * lo lee y lo escribe sobre la `SurveyQuestion` de la empresa, que es de donde
     * cuelgan las respuestas historicas. Desde aca es un campo mas del mismo submit.
     */
    // Que le pide la app al chofer para poder cerrar una entrega. Los tres controles son
    // independientes a proposito: hay operaciones que quieren la foto como prueba pero no
    // la firma -retiro en bodega-, y otras al reves.
    //
    // Hasta ahora esto estaba hardcodeado en la app (firma + nombre + RUT + 2 fotos,
    // heredado de Appery), asi que los valores que llegan por defecto son exactamente
    // esos: entrar aca y guardar sin tocar nada no cambia nada.
    const renderPodSettings = () => {
        if(!settings) return null;

        const fotos = settings.pod_required_photos ?? 2;

        return (
            <Grid container spacing={3}>
                <Grid item xs={12} md={10} lg={7}>
                    <Typography variant="h6" sx={{mb: 2}}>Cierre de entrega</Typography>

                    <Grid container spacing={3} alignItems={"center"} sx={{mb: 2}}>
                        <Grid item xs={12} sm={4} lg={4}>
                            <FormLabel>Pedir firma de quien recibe</FormLabel>
                        </Grid>
                        <Grid item xs={12} sm={8} lg={8}>
                            <RadioGroup row>
                                <FormControlLabel value={true} control={<Radio checked={settings.pod_require_signature === true} onClick={() => setSettings({...settings, pod_require_signature: true})}/>} label="Si"/>
                                <FormControlLabel value={false} control={<Radio checked={settings.pod_require_signature === false} onClick={() => setSettings({...settings, pod_require_signature: false})}/>} label="No"/>
                            </RadioGroup>
                        </Grid>
                    </Grid>

                    <Grid container spacing={3} alignItems={"center"} sx={{mb: 2}}>
                        <Grid item xs={12} sm={4} lg={4}>
                            <FormLabel>Pedir nombre y RUT de quien recibe</FormLabel>
                        </Grid>
                        <Grid item xs={12} sm={8} lg={8}>
                            <RadioGroup row>
                                <FormControlLabel value={true} control={<Radio checked={settings.pod_require_receiver === true} onClick={() => setSettings({...settings, pod_require_receiver: true})}/>} label="Si"/>
                                <FormControlLabel value={false} control={<Radio checked={settings.pod_require_receiver === false} onClick={() => setSettings({...settings, pod_require_receiver: false})}/>} label="No"/>
                            </RadioGroup>
                        </Grid>
                    </Grid>

                    <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2}}>
                        <Grid item xs={12} sm={4} lg={4}>
                            <FormLabel>Fotos obligatorias</FormLabel>
                        </Grid>
                        <Grid item xs={12} sm={8} lg={8}>
                            <Select
                                fullWidth
                                variant="standard"
                                value={fotos}
                                onChange={(e) => setSettings({...settings, pod_required_photos: Number(e.target.value)})}
                            >
                                {POD_PHOTO_OPTIONS.map((n) => (
                                    <MenuItem key={n} value={n}>
                                        {n === 0 ? 'No pedir fotos' : `${n} ${n === 1 ? 'foto' : 'fotos'}`}
                                    </MenuItem>
                                ))}
                            </Select>
                            <Typography variant="caption" color="text.secondary">
                                {fotos === 0
                                    ? 'El chofer puede cerrar la entrega sin sacar ninguna foto.'
                                    : `El chofer no puede cerrar la entrega hasta sacar ${fotos === 1 ? 'una foto' : `${fotos} fotos`}.`}
                            </Typography>
                        </Grid>
                    </Grid>

                    {renderDeliveryRangeSettings()}
                </Grid>
            </Grid>
        )
    }

    // Que tan lejos del punto y que tan corrido de la hora planificada puede cerrar el chofer
    // (Deliveries::RangeCheck en el backend). Vacio es "no se mide". Nunca bloquea el cierre:
    // a lo mas, con "Obligatorio", le pide al chofer el motivo.
    const renderDeliveryRangeSettings = () => {
        const numero = (field) => (e) => {
            const v = e.target.value.replace(/\D/g, '');
            setSettings({...settings, [field]: v === '' ? null : Number(v)});
        };
        const midiendo = settings.delivery_range_distance_m || settings.delivery_range_time_min;

        return (
            <React.Fragment>
                <Typography variant="subtitle1" sx={{mt: 3, mb: 1, fontWeight: 600}}>Rango de la entrega</Typography>
                <Typography variant="caption" color="text.secondary" component="p" sx={{mb: 2}}>
                    Compara dónde y cuándo el chofer cierra la entrega con el punto de entrega y la hora planificada.
                    Las entregas fuera de rango se marcan en el dashboard y no cuentan en la comparación de km.
                </Typography>

                <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2}}>
                    <Grid item xs={12} sm={4} lg={4}>
                        <FormLabel>Distancia máxima al punto</FormLabel>
                    </Grid>
                    <Grid item xs={12} sm={8} lg={8}>
                        <TextField
                            fullWidth
                            variant="standard"
                            value={settings.delivery_range_distance_m ?? ''}
                            onChange={numero('delivery_range_distance_m')}
                            placeholder="Sin límite"
                            InputProps={{ endAdornment: <InputAdornment position="end">metros</InputAdornment> }}
                            inputProps={{ inputMode: 'numeric' }}
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2}}>
                    <Grid item xs={12} sm={4} lg={4}>
                        <FormLabel>Diferencia máxima con la hora planificada</FormLabel>
                    </Grid>
                    <Grid item xs={12} sm={8} lg={8}>
                        <TextField
                            fullWidth
                            variant="standard"
                            value={settings.delivery_range_time_min ?? ''}
                            onChange={numero('delivery_range_time_min')}
                            placeholder="Sin límite"
                            InputProps={{ endAdornment: <InputAdornment position="end">minutos antes o después</InputAdornment> }}
                            inputProps={{ inputMode: 'numeric' }}
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={3} alignItems={"center"} sx={{mb: 2}}>
                    <Grid item xs={12} sm={4} lg={4}>
                        <FormLabel>Avisar al chofer en la app</FormLabel>
                    </Grid>
                    <Grid item xs={12} sm={8} lg={8}>
                        <RadioGroup row>
                            <FormControlLabel value={true} control={<Radio disabled={!midiendo} checked={settings.delivery_range_alert === true} onClick={() => setSettings({...settings, delivery_range_alert: true})}/>} label="Si"/>
                            <FormControlLabel value={false} control={<Radio disabled={!midiendo} checked={settings.delivery_range_alert !== true} onClick={() => setSettings({...settings, delivery_range_alert: false})}/>} label="No"/>
                        </RadioGroup>
                        <Typography variant="caption" color="text.secondary">
                            {!midiendo
                                ? 'Configura una distancia o un tiempo para poder avisar.'
                                : settings.delivery_range_alert
                                    ? 'Al firmar fuera de rango, la app le muestra al chofer cuánto se alejó.'
                                    : 'El chofer no ve nada; las entregas fuera de rango solo quedan marcadas en la web.'}
                        </Typography>
                    </Grid>
                </Grid>

                <Grid container spacing={3} alignItems={"center"} sx={{mb: 2}}>
                    <Grid item xs={12} sm={4} lg={4}>
                        <FormLabel>Respetar el rango</FormLabel>
                    </Grid>
                    <Grid item xs={12} sm={8} lg={8}>
                        <RadioGroup row>
                            <FormControlLabel value={false} control={<Radio disabled={!midiendo || !settings.delivery_range_alert} checked={settings.delivery_range_reason_required !== true} onClick={() => setSettings({...settings, delivery_range_reason_required: false})}/>} label="Opcional"/>
                            <FormControlLabel value={true} control={<Radio disabled={!midiendo || !settings.delivery_range_alert} checked={settings.delivery_range_reason_required === true} onClick={() => setSettings({...settings, delivery_range_reason_required: true})}/>} label="Obligatorio"/>
                        </RadioGroup>
                        <Typography variant="caption" color="text.secondary">
                            {settings.delivery_range_reason_required
                                ? 'Fuera de rango, el chofer tiene que escribir el motivo para poder cerrar. La entrega nunca se bloquea.'
                                : 'Fuera de rango, el chofer ve el aviso y puede cerrar igual.'}
                        </Typography>
                    </Grid>
                </Grid>
            </React.Fragment>
        );
    }

    const renderDriverTripsWindowSettings = () => {
        // Mientras carga, settings es null (igual que en renderPodSettings).
        if(!settings) return null;

        const dias = settings.driver_trips_window_days ?? 7;
        return (
            <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2}}>
                <Grid item xs={12} sm={4} lg={4}>
                    <FormLabel>Días de viajes que ve el chofer</FormLabel>
                </Grid>
                <Grid item xs={12} sm={8} lg={8}>
                    <Select
                        fullWidth
                        variant="standard"
                        value={dias}
                        onChange={(e) => setSettings({...settings, driver_trips_window_days: Number(e.target.value)})}
                    >
                        {DRIVER_TRIPS_WINDOW_OPTIONS.map((n) => (
                            <MenuItem key={n} value={n}>
                                {n === 1 ? 'Solo hoy' : `Hoy y los ${n - 1} ${n - 1 === 1 ? 'día anterior' : 'días anteriores'} (${n} días)`}
                            </MenuItem>
                        ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary">
                        La lista de viajes de la app muestra los viajes de esos días, más los programados a futuro.
                    </Typography>
                </Grid>
            </Grid>
        )
    }

    const renderSurveySettings = () => {
        if(settings) {
            return (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={10} lg={7}>
                        <Typography variant="h6" sx={{mb: 2}}>Evaluación del servicio</Typography>

                        <Grid container spacing={3} alignItems={"center"} sx={{mb: 2}}>
                            <Grid item xs={12} sm={4} lg={4}>
                                <FormLabel>Pedir evaluación al firmar</FormLabel>
                            </Grid>
                            <Grid item xs={12} sm={8} lg={8}>
                                <RadioGroup row>
                                    <FormControlLabel value={true} control={<Radio checked={settings.survey_rating_enabled === true} onClick={() => setSettings({...settings, survey_rating_enabled: true})}/>} label="Si"/>
                                    <FormControlLabel value={false} control={<Radio checked={settings.survey_rating_enabled === false} onClick={() => setSettings({...settings, survey_rating_enabled: false})}/>} label="No"/>
                                </RadioGroup>
                            </Grid>
                        </Grid>

                        <Grid container spacing={3} alignItems={"baseline"} sx={{mb: 2}}>
                            <Grid item xs={12} sm={4} lg={4}>
                                <FormLabel>Pregunta</FormLabel>
                            </Grid>
                            <Grid item xs={12} sm={8} lg={8}>
                                <TextField
                                    fullWidth
                                    error={false}
                                    variant="standard"
                                    placeholder={SURVEY_RATING_DEFAULT_QUESTION}
                                    helperText={settings.survey_rating_enabled
                                        ? 'Es lo que lee el cliente sobre las estrellas, en el teléfono del chofer.'
                                        : 'Se guarda, pero no se le muestra a nadie mientras la evaluación esté en No.'}
                                    value={settings.survey_rating_question ?? ''}
                                    onChange={(e) => setSettings({...settings, survey_rating_question: e.target.value})}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            )
        }
    }

    const renderDocumentTypeVisibilitySettings = () => {
        if(!settings) return null;

        const hidden = settings.superadmin_only_document_types || [];
        const setVisibility = (key, onlySuperadmin) => setSettings({
            ...settings,
            superadmin_only_document_types: onlySuperadmin ? _.uniq([...hidden, key]) : _.without(hidden, key),
        });

        return (
            <Grid container spacing={3}>
                <Grid item xs={12} md={10} lg={7}>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                        Quién ve cada tipo de documento en Pedidos y en Planificación. Los viajes ya armados muestran siempre todas sus paradas.
                    </Typography>
                    {DOCUMENT_TYPES.map((type) => {
                        const onlySuperadmin = hidden.includes(type.key);
                        return (
                            <Grid key={type.key} container spacing={3} alignItems={"baseline"} sx={{mb: 1}}>
                                <Grid item xs={12} sm={5} lg={5}>
                                    <FormLabel>{type.label}</FormLabel>
                                </Grid>
                                <Grid item xs={12} sm={7} lg={7}>
                                    <Select
                                        fullWidth
                                        variant="standard"
                                        value={onlySuperadmin ? 'superadmin' : 'all'}
                                        onChange={(e) => setVisibility(type.key, e.target.value === 'superadmin')}
                                    >
                                        <MenuItem value="all">Todos los perfiles</MenuItem>
                                        <MenuItem value="superadmin">Solo superadmin</MenuItem>
                                    </Select>
                                    {type.key === 'sin_documento' && onlySuperadmin && (
                                        <Typography variant="caption" color="warning.main">
                                            Ojo: en las compañías con Relbase casi todos los pedidos quedan sin documento hasta el pickeo, así que se ocultarían casi todos.
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        );
                    })}
                </Grid>
            </Grid>
        )
    }

    const renderOperationsSettings = () => {
        return (
            <React.Fragment>
                <SettingsCard title="Requisitos de planificación" isLoading={isLoading} fitContent>
                    {renderPlanificationSettings()}
                </SettingsCard>

                <SettingsCard title="Requisito de preparación de pedidos" isLoading={isLoading}>
                    {renderMobileAppSettings()}
                </SettingsCard>

                <SettingsCard title="Configuración de Retail" minScrollHeight={200}>
                    <RetailCustomersSettings companyId={company_id} />
                </SettingsCard>

                {isSuperadmin && (
                    <SettingsCard title="Tipos de documento visibles" isLoading={isLoading} fitContent>
                        {renderDocumentTypeVisibilitySettings()}
                    </SettingsCard>
                )}
            </React.Fragment>
        )
    }

    const renderAdminSettings = () => {
        return (
            <React.Fragment>
                <SettingsCard title="Empresa" isLoading={isLoading}>
                    {renderCompanySettings()}
                </SettingsCard>

                <SettingsCard title="Facturación" minScrollHeight={400}>
                    {renderBillingSettings()}
                </SettingsCard>

                <SettingsCard title="Notificaciones y correos electrónicos" isLoading={isLoading} minScrollHeight={395}>
                    {renderNotifSettings()}
                </SettingsCard>

                <SettingsCard title="App móvil" isLoading={isLoading} fitContent>
                    {renderDriverTripsWindowSettings()}
                    {renderPodSettings()}
                    {renderSurveySettings()}
                </SettingsCard>
            </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            {section === OPERATIONS_SECTION && renderOperationsSettings()}

            {section === ADMIN_SECTION && !restrictedView && renderAdminSettings()}
            
            <Div sx={{ position: 'fixed', bottom: 20, right: 60, zIndex: 1300, }}>
                <LoadingButton
                    loading={saving}
                    variant="contained"
                    color="success"
                    size="medium"
                    endIcon={<SaveOutlinedIcon />}
                    loadingPosition="end"
                    onClick={() => saveCompanySettings()}>
                        { saving ? 'Guardando' : 'Guardar' }
                </LoadingButton>
            </Div>

            {renderDropoffCarrierEditModal()}

            <ConfirmDialog
                open={Boolean(confirmDeleteDropoffCarrier)}
                title="Eliminar compañía dropoff"
                message={
                    <>
                        ¿Estás seguro que deseas eliminar la compañía dropoff <strong>{confirmDeleteDropoffCarrier?.label || confirmDeleteDropoffCarrier?.key}</strong>?
                        <br />
                        Dejará de aparecer al planificar. Los pedidos que ya la tengan asignada no cambian.
                    </>
                }
                loading={deletingDropoffCarrierId === confirmDeleteDropoffCarrier?.id}
                onConfirm={() => deleteDropoffCarrier(confirmDeleteDropoffCarrier)}
                onClose={() => setConfirmDeleteDropoffCarrier(null)}
            />

        </React.Fragment>
    );
};

export default EditCompany;
